import React, { useState, useEffect } from 'react';

function PlantillaForm({ plantilla, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    ruta_archivo: '',
    campos_personalizables: [],
    activa: true,
  });

  const [nuevoCampo, setNuevoCampo] = useState('');
  const [archivo, setArchivo] = useState(null);

  useEffect(() => {
    if (plantilla) {
      setFormData({
        nombre: plantilla.nombre || '',
        descripcion: plantilla.descripcion || '',
        ruta_archivo: plantilla.ruta_archivo || '',
        campos_personalizables: plantilla.campos_personalizables || [],
        activa: plantilla.activa !== undefined ? plantilla.activa : true,
      });
    }
  }, [plantilla]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.docx')) {
        alert('Por favor, selecciona un archivo Word (.docx)');
        return;
      }
      setArchivo(file);
      // Si no hay ruta_archivo, usar el nombre del archivo
      if (!formData.ruta_archivo) {
        setFormData(prev => ({
          ...prev,
          ruta_archivo: file.name,
        }));
      }
    }
  };

  const handleAddCampo = () => {
    if (nuevoCampo.trim() && !formData.campos_personalizables.includes(nuevoCampo.trim())) {
      setFormData(prev => ({
        ...prev,
        campos_personalizables: [...prev.campos_personalizables, nuevoCampo.trim()],
      }));
      setNuevoCampo('');
    }
  };

  const handleRemoveCampo = (campo) => {
    setFormData(prev => ({
      ...prev,
      campos_personalizables: prev.campos_personalizables.filter(c => c !== campo),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      alert('El nombre de la plantilla es obligatorio.');
      return;
    }
    if (!plantilla && !archivo && !formData.ruta_archivo.trim()) {
      alert('Debes subir un archivo Word (.docx) o indicar la ruta del archivo.');
      return;
    }

    const data = {
      nombre: formData.nombre,
      descripcion: formData.descripcion || null,
      ruta_archivo: plantilla && !archivo ? formData.ruta_archivo : (formData.ruta_archivo || ''),
      campos_personalizables: formData.campos_personalizables,
      activa: formData.activa,
    };

    // Si hay un archivo nuevo, se enviará junto con los datos
    onSubmit(data, archivo);
  };

  return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{plantilla ? 'Editar Plantilla' : 'Crear Plantilla'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre *</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Descripción</label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Archivo Word (.docx) {!plantilla && '*'}</label>
            <input
              type="file"
              accept=".docx"
              onChange={handleFileChange}
            />
            {archivo && (
              <small style={{ color: 'green', display: 'block', marginTop: '5px' }}>
                ✓ Archivo seleccionado: {archivo.name}
              </small>
            )}
            {plantilla && !archivo && (
              <>
                <small style={{ display: 'block', marginTop: '10px' }}>
                  Archivo actual: {formData.ruta_archivo}
                </small>
                <small className="help-text">
                  Si no seleccionas un archivo nuevo, se mantendrá el archivo actual.
                </small>
              </>
            )}
            {!plantilla && !archivo && (
              <small className="help-text">
                O especifica la ruta del archivo si ya está en el servidor:
              </small>
            )}
            {!plantilla && !archivo && (
              <input
                type="text"
                name="ruta_archivo"
                value={formData.ruta_archivo}
                onChange={handleChange}
                placeholder="ej: templates/plantilla_ejemplo.docx"
                style={{ marginTop: '5px' }}
              />
            )}
          </div>

          <div className="form-group">
            <label>Campos Personalizables</label>
            <div className="campos-personalizados">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={nuevoCampo}
                  onChange={(e) => setNuevoCampo(e.target.value)}
                  placeholder="Nombre del campo (ej: nombre, email, fecha)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCampo();
                    }
                  }}
                />
                <button type="button" className="btn btn-secondary" onClick={handleAddCampo}>
                  Agregar
                </button>
              </div>
              {formData.campos_personalizables.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {formData.campos_personalizables.map((campo, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '5px 0',
                      borderBottom: '1px solid #eee'
                    }}>
                      <span>{campo}</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveCampo(campo)}
                      >
                        Eliminar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <small className="help-text">
                Estos campos se pueden usar en el archivo Word con el formato {'{{nombre_campo}}'}.
                Ejemplo: Si defines "nombre", en tu Word puedes usar {'{{nombre}}'}.
              </small>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="activa"
                checked={formData.activa}
                onChange={handleChange}
              />
              Plantilla activa
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {plantilla ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PlantillaForm;

